const InterviewProcessSection = () => {
  const steps = [
    {
      number: '1',
      title: 'Book Your Session',
      description: 'Choose a time that works for you and select your preferred tech stack (React, JavaScript, TypeScript, etc.)',
      duration: '2 minutes',
      icon: '📅'
    },
    {
      number: '2',
      title: 'Pre-Interview Briefing',
      description: 'Receive a confirmation email with the meeting link and what to expect. Prepare your coding environment.',
      duration: '1 day before',
      icon: '📧'
    },
    {
      number: '3',
      title: 'The Mock Interview',
      description: 'Experience a realistic interview with behavioral questions, technical discussions, and live coding challenges.',
      duration: '45-90 minutes',
      icon: '💻'
    },
    {
      number: '4',
      title: 'Immediate Feedback Session',
      description: 'Get real-time feedback on your performance, strengths, and specific areas to improve.',
      duration: '15-30 minutes',
      icon: '📝'
    },
    {
      number: '5',
      title: 'Detailed Report',
      description: 'Receive a comprehensive written report with scores, recommendations, and resources for improvement.',
      duration: 'Within 24 hours',
      icon: '📊'
    }
  ];

  const includedFeatures = [
    {
      category: 'Technical Assessment',
      items: [
        'Live coding challenges',
        'System design discussions (Pro & Enterprise)',
        'Data structures & algorithms questions',
        'Best practices review'
      ],
      icon: '💻'
    },
    {
      category: 'Behavioral Interview',
      items: [
        'Common behavioral questions',
        'STAR method coaching',
        'Team fit assessment',
        'Career story refinement'
      ],
      icon: '💬'
    },
    {
      category: 'Feedback & Coaching',
      items: [
        'Detailed performance scoring',
        'Specific improvement areas',
        'Interview strategy tips',
        'Resume & LinkedIn review (Pro & Enterprise)'
      ],
      icon: '🎯'
    }
  ];

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How It Works: Simple, Effective, Results-Driven
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From booking to landing your offer, here's your journey with us
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-20">
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-1 bg-gradient-to-r from-orange-200 via-orange-400 to-orange-600" style={{ width: '80%', margin: '0 10%' }}></div>

            {/* Steps */}
            <div className="grid md:grid-cols-5 gap-8 relative">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Step Circle */}
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg mb-4 relative z-10">
                      {step.icon}
                    </div>

                    {/* Step Number Badge */}
                    <div className="absolute top-0 right-0 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-gray-900 font-bold text-sm shadow-md">
                      {step.number}
                    </div>

                    {/* Content */}
                    <div className="text-center">
                      <h3 className="font-bold text-lg text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {step.description}
                      </p>
                      <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                        {step.duration}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-gradient-to-br from-gray-50 to-orange-50 rounded-2xl p-10">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center">
            What's Included in Every Session
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            {includedFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  {feature.category}
                </h4>
                <ul className="space-y-3">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-2">
                      <span className="text-green-500 font-bold flex-shrink-0 mt-1">✓</span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InterviewProcessSection;
